import { useEffect, useMemo, useRef } from "react";

import { css } from "@compiled/react";
import debounce from "lodash/debounce";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import {
    useInputValues,
    useInputSetters
} from "@/components/usage-based-pricing-calculator/src/hooks/hooks";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import {
    useAiAutomationTracking,
    useAiChatAgentsTracking
} from "../hooks/tracking";

import { messages } from "./feature-estimation.messages";
import { NumericInput } from "./numeric-input";

const sectionStyles = css({
    paddingBlock: token("space.400"),
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`
});

const rovoFeaturesLinkStyles = css({
    "textDecoration": "none",
    "paddingBlock": token("space.100"),
    "display": "inline-block",
    "&:hover": {
        textDecoration: "none"
    }
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const cardsContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),
    marginBlockEnd: token("space.400")
});

const featureCardStyles = css({
    backgroundColor: wacToken("color.neutral.light0"),
    border: `1px solid ${wacToken("color.neutral.light400")}`,
    borderRadius: "8px",
    overflow: "hidden"
});

const cardContentStyles = css({
    padding: token("space.250")
});

const cardTitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 600,
    fontSize: "16px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.200")
});

const dropdownsRowStyles = css({
    display: "flex",
    gap: token("space.200"),
    flexWrap: "wrap"
});

const dropdownGroupStyles = css({
    flex: 1,
    minWidth: "140px"
});

const dropdownLabelStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.100"),
    display: "block"
});

export const FeatureEstimation = () => {
    const intl = useIntl();
    const inputs = useInputValues();
    const setters = useInputSetters();
    const { fireChatAdoptionChangedEvent, fireChatQueriesPerUserChangedEvent } =
        useAiChatAgentsTracking();
    const {
        fireAutomationActiveRulesChangedEvent,
        fireAutomationRunsPerRuleChangedEvent
    } = useAiAutomationTracking();

    const fireChatAdoptionChangedEventRef = useRef(
        fireChatAdoptionChangedEvent
    );
    fireChatAdoptionChangedEventRef.current = fireChatAdoptionChangedEvent;
    const fireChatQueriesPerUserChangedEventRef = useRef(
        fireChatQueriesPerUserChangedEvent
    );
    fireChatQueriesPerUserChangedEventRef.current =
        fireChatQueriesPerUserChangedEvent;
    const fireAutomationActiveRulesChangedEventRef = useRef(
        fireAutomationActiveRulesChangedEvent
    );
    fireAutomationActiveRulesChangedEventRef.current =
        fireAutomationActiveRulesChangedEvent;
    const fireAutomationRunsPerRuleChangedEventRef = useRef(
        fireAutomationRunsPerRuleChangedEvent
    );
    fireAutomationRunsPerRuleChangedEventRef.current =
        fireAutomationRunsPerRuleChangedEvent;

    const debouncedFireChatAdoptionChangedEvent = useMemo(
        () =>
            debounce((value: number) => {
                fireChatAdoptionChangedEventRef.current(value);
            }, 1000),
        []
    );
    const debouncedFireChatQueriesPerUserChangedEvent = useMemo(
        () =>
            debounce((value: number) => {
                fireChatQueriesPerUserChangedEventRef.current(value);
            }, 1000),
        []
    );
    const debouncedFireAutomationActiveRulesChangedEvent = useMemo(
        () =>
            debounce((value: number) => {
                fireAutomationActiveRulesChangedEventRef.current(value);
            }, 1000),
        []
    );
    const debouncedFireAutomationRunsPerRuleChangedEvent = useMemo(
        () =>
            debounce((value: number) => {
                fireAutomationRunsPerRuleChangedEventRef.current(value);
            }, 1000),
        []
    );

    useEffect(() => {
        debouncedFireChatAdoptionChangedEvent(inputs.chatAdoptionPercent);
    }, [inputs.chatAdoptionPercent, debouncedFireChatAdoptionChangedEvent]);

    useEffect(() => {
        debouncedFireChatQueriesPerUserChangedEvent(
            inputs.chatUsersPerRulePerMonth
        );
    }, [
        inputs.chatUsersPerRulePerMonth,
        debouncedFireChatQueriesPerUserChangedEvent
    ]);

    useEffect(() => {
        debouncedFireAutomationActiveRulesChangedEvent(
            inputs.automationActiveRules
        );
    }, [
        inputs.automationActiveRules,
        debouncedFireAutomationActiveRulesChangedEvent
    ]);

    useEffect(() => {
        debouncedFireAutomationRunsPerRuleChangedEvent(
            inputs.automationRunsPerRulePerMonth
        );
    }, [
        inputs.automationRunsPerRulePerMonth,
        debouncedFireAutomationRunsPerRuleChangedEvent
    ]);

    return (
        <div css={sectionStyles}>
            <Heading size="medium" as="h2">
                <span css={titleStyles}>
                    <FormattedMessage {...messages.featureEstimationTitle} />
                </span>
            </Heading>
            <Link
                type="inline"
                href="https://support.atlassian.com/rovo/docs/rovo-concepts/"
                text="More about Rovo features"
                aria-label="More about Rovo features"
                css={rovoFeaturesLinkStyles}
            />

            <div css={cardsContainerStyles}>
                {/* AI Chat & Agents */}
                <div css={featureCardStyles}>
                    <div css={cardContentStyles}>
                        <Heading size="small" as="h3">
                            <span css={cardTitleStyles}>
                                <FormattedMessage
                                    {...messages.aiChatAndAgentsTitle}
                                />
                            </span>
                        </Heading>
                        <div css={dropdownsRowStyles}>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="chatAdoptionPercent"
                                >
                                    <FormattedMessage
                                        {...messages.expectedAdoptionPercentLabel}
                                    />
                                </label>
                                <NumericInput
                                    id="chatAdoptionPercent"
                                    aria-label={intl.formatMessage(
                                        messages.expectedAdoptionPercentLabel
                                    )}
                                    min={0}
                                    max={1}
                                    asPercentage
                                    value={inputs.chatAdoptionPercent}
                                    onChange={setters.setChatAdoptionPercent}
                                />
                            </div>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="chatUsersPerRulePerMonth"
                                >
                                    <FormattedMessage
                                        {...messages.usesPerMonthLabel}
                                    />
                                </label>
                                <NumericInput
                                    id="chatUsersPerRulePerMonth"
                                    aria-label={intl.formatMessage(
                                        messages.usesPerMonthLabel
                                    )}
                                    min={0}
                                    max={90000}
                                    value={inputs.chatUsersPerRulePerMonth}
                                    onChange={
                                        setters.setChatUsersPerRulePerMonth
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Automation */}
                <div css={featureCardStyles}>
                    <div css={cardContentStyles}>
                        <Heading size="small" as="h3">
                            <span css={cardTitleStyles}>
                                <FormattedMessage
                                    {...messages.aiAutomationInAgentsTitle}
                                />
                            </span>
                        </Heading>
                        <div css={dropdownsRowStyles}>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="automationActiveRules"
                                >
                                    <FormattedMessage
                                        {...messages.activeAutomationRulesLabel}
                                    />
                                </label>
                                <NumericInput
                                    id="automationActiveRules"
                                    aria-label={intl.formatMessage(
                                        messages.activeAutomationRulesLabel
                                    )}
                                    min={0}
                                    max={90000}
                                    value={inputs.automationActiveRules}
                                    onChange={setters.setAutomationActiveRules}
                                />
                            </div>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="automationRunsPerRulePerMonth"
                                >
                                    <FormattedMessage
                                        {...messages.ruleRunsPerMonthLabel}
                                    />
                                </label>
                                <NumericInput
                                    id="automationRunsPerRulePerMonth"
                                    aria-label={intl.formatMessage(
                                        messages.ruleRunsPerMonthLabel
                                    )}
                                    min={0}
                                    max={90000}
                                    value={inputs.automationRunsPerRulePerMonth}
                                    onChange={
                                        setters.setAutomationRunsPerRulePerMonth
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
